import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { AgentOmnichannel } from '../../../../i18n'
import { EMAIL_REGEX } from '../../../NonReact/constants/Regexes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import FormRow from '../../Common/Components/FormRow'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { LanguageContext } from '../../Common/Contexts'
import {
    ALLOW_CONCURRENT_CHANNELS_FIELD_NAME,
    CHAT_ID_ON_CHAT_SYSTEM_FIELD_NAME,
    CHAT_INTEGRATION_TYPE_FIELD_NAME,
    EMAIL_ADDRESS_FIELD_NAME,
    EMAIL_ID_ON_EMAIL_SYSTEM_FIELD_NAME,
    EMAIL_INTEGRATION_TYPE_FIELD_NAME
} from '../Constants/FieldNames'
import { ChatIntegrationTypes, EmailIntegrationTypes } from '../Constants/OmnichannelTypes'
import { ConfigureAgentsContext, ConfigureAgentsContextType } from '../Contexts/ConfigureAgentsContext'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'

export const AgentsOmnichannelTab: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AgentOmnichannel.NAME)

    const { watch }: UseFormMethods = useFormContext()

    const {
        viewOnly,
        serverTypeConfig: {
            omnichannel: {
                isAllowAnsweringConcurrentChannels,
                chatIntegration: { isType: isChatType, isId: isChatID },
                emailIntegration: { isType: isEmailType, isId: isEmailID, isEmailAddress }
            }
        }
    }: ConfigureAgentsContextType = useContext(ConfigureAgentsContext)

    const availableChatIntegrations: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                label: language.get(AgentOmnichannel.AgentOmnichannel_Chat_IntegrationTypes_None),
                value: ChatIntegrationTypes.NONE
            },
            {
                label: language.get(AgentOmnichannel.AgentOmnichannel_Chat_IntegrationTypes_LiveChat),
                value: ChatIntegrationTypes.LIVE_CHAT
            }
        ],
        []
    )

    const availableEmailIntegrations: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                label: language.get(AgentOmnichannel.AgentOmnichannel_Email_IntegrationTypes_None),
                value: EmailIntegrationTypes.NONE
            },
            {
                label: language.get(AgentOmnichannel.AgentOmnichannel_Email_IntegrationTypes_Pop3),
                value: EmailIntegrationTypes.POP_3
            }
        ],
        []
    )

    const chatIntegrationValue: string = watch(CHAT_INTEGRATION_TYPE_FIELD_NAME)
    const emailIntegrationValue: string = watch(EMAIL_INTEGRATION_TYPE_FIELD_NAME)

    return (
        <div className='ca-omnichannel-tab tab-area'>
            {isAllowAnsweringConcurrentChannels ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(AgentOmnichannel.AgentOmnichannel_AllowAnsweringConcurrent)}
                        name={ALLOW_CONCURRENT_CHANNELS_FIELD_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isChatType ? (
                <FormRow>
                    <OutlinedSelect
                        options={availableChatIntegrations}
                        label={language.get(AgentOmnichannel.AgentOmnichannel_Chat_IntegrationType)}
                        name={CHAT_INTEGRATION_TYPE_FIELD_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isChatID ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={
                            chatIntegrationValue === ChatIntegrationTypes.LIVE_CHAT
                                ? language.get(AgentOmnichannel.AgentOmnichannel_Chat_LiveChatAgentID)
                                : language.get(AgentOmnichannel.AgentOmnichannel_Chat_IDOnChatSystem)
                        }
                        inputName={CHAT_ID_ON_CHAT_SYSTEM_FIELD_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isEmailType ? (
                <FormRow>
                    <Select
                        options={availableEmailIntegrations}
                        label={language.get(AgentOmnichannel.AgentOmnichannel_Email_IntegrationType)}
                        name={EMAIL_INTEGRATION_TYPE_FIELD_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {isEmailAddress ? (
                <FormRow>
                    <ValidationErrors name={EMAIL_ADDRESS_FIELD_NAME}>
                        <OutlinedTextInput
                            labelLeft={language.get(AgentOmnichannel.AgentOmnichannel_Email_EmailAddress)}
                            inputName={EMAIL_ADDRESS_FIELD_NAME}
                            disabled={viewOnly}
                            rules={{
                                validate: {
                                    isEmailAddress: (email: string): ValidateResult => {
                                        if (isStringNotEmpty(email) && !email?.match(EMAIL_REGEX)) {
                                            return language.get(
                                                AgentOmnichannel.AgentOmnichannel_Email_EmailAddress_Invalid
                                            )
                                        }

                                        return undefined
                                    }
                                }
                            }}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {isEmailID && emailIntegrationValue !== EmailIntegrationTypes.POP_3 ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(AgentOmnichannel.AgentOmnichannel_Email_IDOnEmailSystem)}
                        inputName={EMAIL_ID_ON_EMAIL_SYSTEM_FIELD_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
